import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Alert } from 'react-native';
import { StepWizardModal } from '@/components/common/StepWizardModal';
import { PostItemMediaItem } from '@/types/residents/marketplaceListing';
import { useMaintenanceForm } from '@/hooks/residents/useMaintenanceForm';
import { useMaintenanceSubmit } from '@/hooks/residents/useMaintenanceSubmit';
import { useMaintenanceValidation } from '@/hooks/residents/useMaintenanceValidation';
import { useAppSelector } from '@/store';
import { MAINTENANCE_CONSTANTS } from '@/constants/residents';
import { getAllSubCategories } from '@/utils/residents/maintenance/maintenance';

import { StepCategorySelection } from '@/components/residents/maintenance/mobile/steps/StepCategorySelection';
import { StepSeverity } from '@/components/residents/maintenance/mobile/steps/StepSeverity';
import { StepDetails } from '@/components/residents/maintenance/mobile/steps/StepDetails';
import { StepMedia } from '@/components/residents/marketplace/post-item/StepMedia';
import { StepEntryPermission } from '@/components/residents/maintenance/mobile/steps/StepEntryPermission';
import { StepAdditional } from '@/components/residents/maintenance/mobile/steps/StepAdditional';

export type MaintenanceMediaItem = PostItemMediaItem;

type MaintenanceStep = 'category' | 'severity' | 'details' | 'media' | 'entry' | 'additional';

const STEPS: MaintenanceStep[] = ['category', 'severity', 'details', 'media', 'entry', 'additional'];
const TOTAL_STEPS = STEPS.length;

interface MobileCreateMaintenanceModalProps {
  visible: boolean;
  onClose: () => void;
}

export const MobileCreateMaintenanceModal: React.FC<MobileCreateMaintenanceModalProps> = ({
  visible,
  onClose,
}) => {
  const user = useAppSelector((s) => s.auth.user as any);
  
  const {
    description, setDescription,
    location, setLocation,
    category, setCategory,
    subCategory, setSubCategory,
    problem, setProblem,
    severity, setSeverity,
    phoneNumber,
    permissionChoice, setPermissionChoice,
    maintenanceInfo,
    subCategoryOptions: _hookSubCategoryOptions,
    severityOptions,
    loadInfo,
    isInfoError,
    infoErrorStatus,
  } = useMaintenanceForm();

  // For mobile, provide ALL subcategories from ALL categories (flattened)
  // This allows the simplified mobile flow where user picks subcategory without first picking category
  const allSubCategoryOptions = useMemo(() => getAllSubCategories(maintenanceInfo), [maintenanceInfo]);
  const subCategoryOptions = category ? _hookSubCategoryOptions : allSubCategoryOptions;
  
  const { submitTicket, status } = useMaintenanceSubmit();
  const { validateSubcategory, validateSeverity, validateTitle, validateTimeSlots } = useMaintenanceValidation();
  
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [title, setTitle] = useState('');
  const [mediaItems, setMediaItems] = useState<PostItemMediaItem[]>([]);
  const [hasPets, setHasPets] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [requestType, setRequestType] = useState<'maintenance' | 'it'>('maintenance');

  useEffect(() => {
    if (visible) {
      loadInfo();
      resetForm();
    }
  }, [visible, loadInfo]);

  useEffect(() => {
    if (visible && isInfoError && (infoErrorStatus === 500 || infoErrorStatus == null)) {
      console.error('Unable to load maintenance options');
    }
  }, [visible, isInfoError, infoErrorStatus]);

  const currentStep = STEPS[currentStepIndex];
  const isLastStep = currentStep === 'additional';

  const resetForm = useCallback(() => {
    setCurrentStepIndex(0);
    setTitle('');
    setDescription('');
    setLocation('');
    setCategory('');
    setSubCategory('');
    setProblem('');
    setSeverity('');
    setMediaItems([]);
    setHasPets(false);
    setSpecialInstructions('');
    setTimeSlots([]);
    setRequestType('maintenance');
    setPermissionChoice('yes');
  }, [setDescription, setLocation, setCategory, setSubCategory, setProblem, setSeverity, setPermissionChoice]);

  const handleClose = useCallback(() => {
    const hasData = 
      title.trim() !== '' || 
      description.trim() !== '' || 
      mediaItems.length > 0 ||
      category !== '' ||
      severity !== '';

    if (hasData) {
      Alert.alert(
        'Discard Request?',
        'Are you sure you want to discard your work request?',
        [
          { text: 'Cancel', style: 'cancel' },
          { 
            text: 'Discard', 
            style: 'destructive',
            onPress: () => {
              resetForm();
              onClose();
            },
          },
        ]
      );
    } else {
      resetForm();
      onClose();
    }
  }, [title, description, mediaItems, category, severity, resetForm, onClose]);

  const validateCurrentStep = useCallback((): boolean => {
    let result;
    switch (currentStep) {
      case 'category':
        if (!requestType) {
          Alert.alert('Selection Required', 'Please select a category.');
          return false;
        }
        result = validateSubcategory(requestType, subCategoryOptions, subCategory);
        if (!result.valid) {
          Alert.alert('Selection Required', result.message);
          return false;
        }
        return true;
      case 'severity':
        result = validateSeverity(severity);
        if (!result.valid) {
          Alert.alert('Selection Required', result.message);
          return false;
        }
        return true;
      case 'details':
        result = validateTitle(title);
        if (!result.valid) {
          Alert.alert('Title Required', result.message);
          return false;
        }
        return true;
      case 'media':
        return true;
      case 'entry':
        result = validateTimeSlots(permissionChoice, timeSlots);
        if (!result.valid) {
          Alert.alert('Time Slots Required', result.message);
          return false;
        }
        return true;
      case 'additional':
        return true;
      default:
        return true;
    }
  }, [currentStep, requestType, subCategoryOptions, subCategory, severity, title, permissionChoice, timeSlots, validateSubcategory, validateSeverity, validateTitle, validateTimeSlots]);

  const isNextEnabled = useCallback((): boolean => {
    switch (currentStep) {
      case 'category':
        if (requestType === 'maintenance' && subCategoryOptions.length > 0) {
          return !!subCategory;
        }
        return !!requestType;
      case 'severity':
        return !!severity;
      case 'details':
        return title.trim().length > 0;
      case 'media':
        return true;
      case 'entry':
        if (permissionChoice === 'no') {
          return timeSlots.length >= MAINTENANCE_CONSTANTS.MINIMUM_TIME_SLOTS;
        }
        return true;
      case 'additional':
        return true;
      default:
        return true;
    }
  }, [currentStep, requestType, subCategoryOptions, subCategory, severity, title, permissionChoice, timeSlots]);

  const handleNext = useCallback(() => {
    if (!validateCurrentStep()) return;

    if (isLastStep) {
      handleSubmit();
    } else {
      setCurrentStepIndex(prev => Math.min(prev + 1, TOTAL_STEPS - 1));
    }
  }, [validateCurrentStep, isLastStep]);

  const handleBack = useCallback(() => {
    setCurrentStepIndex(prev => Math.max(prev - 1, 0));
  }, []);

  const handleSubmit = useCallback(async () => {
    const result = await submitTicket({
      requestType,
      category,
      subCategory,
      maintenanceInfo,
      location,
      problem,
      severity,
      title,
      description,
      phoneNumber,
      permissionChoice,
      attachments: mediaItems.map(m => ({
        uri: m.uri,
        type: m.type,
        name: m.name,
        file: m.file as any,
      })),
      hasPets,
      specialInstructions,
    });

    if (result.success) {
      resetForm();
      onClose();
    }
  }, [
    requestType, category, subCategory, maintenanceInfo, location, problem,
    severity, title, description, phoneNumber, permissionChoice, mediaItems,
    hasPets, specialInstructions, submitTicket, resetForm, onClose,
  ]);

  const userName = user?.firstName || 'there';
  const maintenancePhone = user?.property?.phone || user?.property?.twilioNumber;

  const renderStepContent = () => {
    switch (currentStep) {
      case 'category':
        return (
          <StepCategorySelection
            userName={userName}
            requestType={requestType}
            onRequestTypeChange={setRequestType}
            subCategory={subCategory}
            onSubCategoryChange={setSubCategory}
            subCategoryOptions={subCategoryOptions}
          />
        );
      case 'severity':
        return (
          <StepSeverity
            severity={severity}
            onSeverityChange={setSeverity}
            severityOptions={severityOptions}
            maintenancePhone={maintenancePhone}
          />
        );
      case 'details':
        return (
          <StepDetails
            title={title}
            onTitleChange={setTitle}
            description={description}
            onDescriptionChange={setDescription}
          />
        );
      case 'media':
        return (
          <StepMedia
            mediaItems={mediaItems}
            onMediaItemsChange={setMediaItems}
            title="Add media for faster resolution"
          />
        );
      case 'entry':
        return (
          <StepEntryPermission
            permissionChoice={permissionChoice}
            onPermissionChoiceChange={setPermissionChoice}
            timeSlots={timeSlots}
            onTimeSlotsChange={setTimeSlots}
          />
        );
      case 'additional':
        return (
          <StepAdditional
            hasPets={hasPets}
            onHasPetsChange={setHasPets}
            specialInstructions={specialInstructions}
            onSpecialInstructionsChange={setSpecialInstructions}
          />
        );
      default:
        return null;
    }
  };

  return (
    <StepWizardModal
      visible={visible}
      onClose={handleClose}
      currentStepIndex={currentStepIndex}
      totalSteps={TOTAL_STEPS}
      onNext={handleNext}
      onBack={handleBack}
      isNextEnabled={isNextEnabled()}
      isSubmitting={status.isLoading}
      isLastStep={isLastStep}
      submitLabel="Submit work request"
    >
      {renderStepContent()}
    </StepWizardModal>
  );
};

export default MobileCreateMaintenanceModal;
